import { defineStore } from 'pinia'
import { router } from '@/router'
import { eventlogAddData } from '@/service/api/track'
import { local } from '@/utils'
import { detectBrowser, formatStandardTime, getDeviceInfo } from '@/utils/common'
import { useRouteStore } from './router'

// 埋点类型
export const TrackerType = {
  page: 'pv',
  btn: 'btn-clk',
  nav: 'nav',
  short: 'short-key',
  area: 'area-clk',
}

// 接口定义（可选）
// 基础数据接口，包含用户和设备的基本信息
interface BaseData {
  // 用户名（可选）
  userName?: string
  // 会话 ID（可选）
  sessionId?: string
  // 设备类型（可选）
  deviceType?: string
  // 操作系统（可选）
  system?: string
  // 浏览器类型（可选）
  browser?: string
  // 屏幕分辨率，包含宽度和高度
  screenResolution: string
  // 来源页面地址（可选）
  referrerFullPath?: string

  // 来源页面标题（可选）
  referrerTitle?: string
}

/**
 * 事件信息接口定义
 */
interface EventInfo {
  // 事件分类，eg: Authenticate身份认证
  eventClass?: string
  // 事件类型，eg: login登录
  eventCategory?: string
  // 事件类别，eg: account
  eventType?: string
  // 事件发生时间
  eventTime?: Date | null
  // 事件结束时间
  eventEndTime?: Date | null
  // 事件持续时间（秒）,主要用于计算用户离开当前页面到回来当前页面间隔事件
  eventDuration?: number
  // 事件内容（目标元素文本）
  eventContent?: string
  // 事件其他内容/动态内容
  eventOtherContent?: string
}

// 菜单点击数据接口，用于记录菜单点击行为
interface MenuClickData {
  // 目标页面的标题（可选）
  toTitle?: string
  // 目标页面的完整路径（可选）
  toFullPath?: string
}

export const useTrackerStore = defineStore('tracker', {
  state: (): {
    base: BaseData
    menu: MenuClickData
    event: EventInfo
    lastSelectedKey: string
  } => {
    const route = unref(router.currentRoute)
    /** 初始化获取设备信息，要在路由跳转前 */
    const ua = navigator.userAgent || navigator.vendor || window.opera
    const isInWeChatMiniProgram = /miniProgram/i.test(ua)
    const isMobile = /iPhone|iPad|iPod|Android|webOS|BlackBerry|IEMobile|Opera Mini/i.test(ua)

    let deviceType = '桌面端'
    if (isInWeChatMiniProgram) {
      deviceType = '手机小程序'
    }
    else if (isMobile) {
      deviceType = '手机H5'
    }
    const info = getDeviceInfo();
    let system = info.system
    // console.log('设备类型:', info.device_type);
    // console.log('操作系统:', info.system);
    // console.log('完整信息:', info);

    const today = formatStandardTime(new Date()).split(' ')[0].replace(/-/g, '') // 输出：20250725
    const userInfo: { username?: string } = local.get('userInfo') || {}
    const userName = userInfo?.username

    const savedSession = local.get('trackerSession') || {}
    const lastDate = savedSession.date
    const lastUserName = savedSession.userName
    let sessionId = savedSession.sessionId
    // 如果没有 sessionId 或者不是今天 或者用户名变了，则重新生成
    if (!sessionId || lastDate !== today || lastUserName !== userName) {
      sessionId = `sessionId_${today}_${userName}`
      local.set('trackerSession', {
        sessionId,
        date: today,
        userName,
      })
    }

    return {
      base: {
        userName,
        sessionId,
        deviceType,
        system,
        browser: detectBrowser(ua),
        screenResolution: `${window.innerWidth} x ${window.innerHeight}`,
        referrerFullPath: route.fullPath || '',
        referrerTitle: route.meta.title || '',
      },
      menu: {
        toTitle: '',
        toFullPath: '',
      },
      event: {
        eventClass: '',
        eventCategory: '',
        eventType: '',
        eventTime: null,
        eventEndTime: null,
        eventDuration: 0,
        eventContent: '',
        eventOtherContent: '',
      },
      lastSelectedKey: '', // 记录上一次点击的菜单 key, 防止重复点击
    }
  },

  actions: {
    init() {
      this.addEventListeners()
      if (local.get('accessToken')) {
        setInterval(() => {
          this.reportEventlog()
        }, 10000) // 每10秒上报一次;
      }
      this.updateSessionId()
    },
    addEventListeners() {
      document.addEventListener('visibilitychange', this.handleVisibilityChange)
      window.addEventListener('resize', this.handleResize)
      window.addEventListener('beforeunload', this.handleBeforeUnload)
    },
    updateSessionId() {
      const today = formatStandardTime(new Date()).split(' ')[0].replace(/-/g, '') // 输出：20250725
      const userInfo: { username?: string } = local.get('userInfo') || {}
      const userName = userInfo?.username

      const savedSession = local.get('trackerSession') || {}
      const lastDate = savedSession.date
      const lastUserName = savedSession.userName
      let sessionId = savedSession.sessionId
      // 如果没有 sessionId 或者不是今天 或者用户名变了，则重新生成
      if (!sessionId || lastDate !== today || lastUserName !== userName) {
        sessionId = `sessionId_${today}_${userName}`
        local.set('trackerSession', {
          sessionId,
          date: today,
          userName,
        })
      }

      this.base.sessionId = sessionId
    },
    /** 刷新来源路由内容  */
    updateReferrer() {
      const route = unref(router.currentRoute)
      this.base.referrerFullPath = route.fullPath || ''
      this.base.referrerTitle = route.meta.title || ''
    },
    /** 获取浏览器屏幕大小 */
    handleResize() {
      this.base.screenResolution = `${window.innerWidth} x ${window.innerHeight}`
    },
    /** 获取离开浏览器和回来浏览器的时间 */
    handleVisibilityChange() {
      if (document.visibilityState === 'hidden') {
        this.event.eventTime = new Date()
        // this.reportBySpecialDirectives('Page_Hibernate', '休眠页面', { eventEndTime: formatStandardTime(this.event.eventEndTime as Date) })
      }
      else if (document.visibilityState === 'visible') {
        this.event.eventEndTime = new Date()
        if (this.event.eventTime) {
          const durationMs = this.event.eventEndTime.getTime() - this.event.eventTime.getTime()
          this.event.eventDuration = Math.round(durationMs / 1000)

          const data = {
            eventTime: formatStandardTime(this.event.eventTime),
            eventEndTime: formatStandardTime(this.event.eventEndTime),
            eventDuration: this.event.eventDuration,
          }
          this.updateReferrer() // 唤醒页面因为无跳转所以得自己更新
          this.reportBySpecialDirectives('Page_Wakeup', '唤醒页面', data)
        }
      }
    },
    /** 浏览器关闭时事件 */
    handleBeforeUnload() {
      // this.event.eventEndTime = new Date()
      console.log('页面即将关闭:', this.event)
    },

    // 初始化/更新用户基础信息
    initBaseInfo() {
      const userInfo: { username?: string } = local.get('userInfo') || {}
      this.base.userName = userInfo?.username || ''
    },

    /** 添加用户信息 */
    addUserInfo(user: any) {
      this.base.userName = user
      this.updateSessionId()
    },

    /** 默认指令上报事件 */
    reportByDirectives(data: any) {
      const eventClass = data.eventClassCode ? TrackEventData[data.eventClassCode].eventClass : '未定义'
      const eventCategory = data.eventCategoryCode ? TrackEventFlatMap[data.eventCategoryCode].eventCategory : '未定义'
      this.report({
        eventClass,
        eventCategory,
        ...data,
      })
    },
    waitForNextNavigation() {
      return new Promise<void>((resolve) => {
        // 注册一个 afterEach 钩子，只执行一次
        const remove = router.afterEach(() => {
          remove() // 自动移除，避免内存泄漏
          resolve() // 通知已完成
        })
      })
    },
    /** 事件点击指令上报事件 */
    async reportBySpecialDirectives(_domType: string, value?: any, _data?: any) {
      this.updateReferrer()
      // console.log(TrackEventFlatMap);
      if (!_domType || !TrackEventFlatMap[_domType]) {
        console.warn(`[Tracking] 不支持的事件类型: ${_domType}`)
        return
      }

      const { eventClassCode, eventClass, eventCategoryCode, eventCategory } = TrackEventFlatMap[_domType]

      if (_domType === 'Page_Jumpto') {
        const fromRouteStore = useRouteStore()

        const toTitle = fromRouteStore.toRoute.meta.title
        const toFullPath = fromRouteStore.toRoute.fullPath
        // console.log('✅ 正确的目标页面:', toTitle, toFullPath)
        this.report({
          eventClassCode,
          eventClass,
          eventCategoryCode,
          eventCategory,
          eventContent: (_data?.public && value ? `会员中心公共区-${value}` : value) ?? null,
          ..._data,
          toTitle,
          toFullPath,
        })
      }
      else {
        this.report({
          eventClassCode,
          eventClass,
          eventCategoryCode,
          eventCategory,
          eventContent: (_data?.public && value ? `会员中心公共区-${value}` : value) ?? null,
          ..._data,
          referrerFullPath: this.base.referrerFullPath || '',
          referrerTitle: this.base.referrerTitle || '',
        })
      }
    },

    /** 上报点击事件 */
    reportClick(value: any) {
      this.report({
        eventClass: '点击事件',
        ...value,
      })
    },

    /** 模拟上报方法 */
    report(data?: any) {
      const fromRouteStore = useRouteStore()

      const fromTitle = fromRouteStore.fromRoute.meta.title
      const fromFullPath = fromRouteStore.fromRoute.fullPath

      const value = {
        ...this.base,
        ...data,
        timeDate: (new Date()).getTime(),
        eventTime: data.eventTime ?? formatStandardTime(new Date()),
        referrerFullPath: data.referrerFullPath || fromFullPath || '', // 优先级：传参 > base > 空
        referrerTitle: data.referrerTitle || fromTitle || '',
        sourceWeb: '会员中心',
      }

      const reportVal = local.get('trackData') || []
      reportVal.push(value)
      // console.log('value: ', value);
      local.set('trackData', reportVal)

      // 用于前端自己查看
      // const reportValWeb = local.get('trackDataWeb') || []
      // reportValWeb.push(value)
      // console.log('value: ', value);
      // local.set('trackDataWeb', reportValWeb)
    },

    /** 上报埋点数据 */
    async reportEventlog() {
      const trackData = local.get('trackData')
      if (!trackData || trackData.length === 0)
        return

      try {
        const res = await eventlogAddData(trackData)
        if (res.code === 1000) {
          local.remove('trackData') // 清除已上报的数据
        }
        else {
          local.remove('trackData') // 上报埋点数据失败
        }
      }
      catch (error) {
        console.error('上报埋点数据失败:', error)
      }
    },
  },
})
